import { useEffect, useRef } from "react";

export interface PressPoint {
  clientX: number;
  clientY: number;
}

export const LONG_PRESS_MS = 450;
export const LONG_PRESS_MOVE_TOLERANCE_PX = 5;

interface Press {
  x: number;
  y: number;
  timer: ReturnType<typeof setTimeout>;
  fired: boolean;
}

// Fires while the finger is still down, and only if it stayed put: drag & drop shares
// the same long-press, so hold + move must stay a drag.
export function useLongPress(onLongPress: ((point: PressPoint) => void) | undefined) {
  const pressRef = useRef<Press | null>(null);
  const onLongPressRef = useRef(onLongPress);
  onLongPressRef.current = onLongPress;

  useEffect(() => () => clearPress(pressRef), []);

  if (!onLongPress) return {};

  return {
    onTouchStart(e: React.TouchEvent) {
      clearPress(pressRef);
      if (e.touches.length !== 1) return;
      const { clientX: x, clientY: y } = e.touches[0];
      const press: Press = {
        x,
        y,
        fired: false,
        timer: setTimeout(() => {
          press.fired = true;
          onLongPressRef.current?.({ clientX: x, clientY: y });
        }, LONG_PRESS_MS),
      };
      pressRef.current = press;
    },
    onTouchMove(e: React.TouchEvent) {
      const press = pressRef.current;
      if (!press || press.fired) return;
      const t = e.touches[0];
      if (
        Math.abs(t.clientX - press.x) > LONG_PRESS_MOVE_TOLERANCE_PX ||
        Math.abs(t.clientY - press.y) > LONG_PRESS_MOVE_TOLERANCE_PX
      ) {
        clearPress(pressRef);
      }
    },
    onTouchEnd(e: React.TouchEvent) {
      const fired = pressRef.current?.fired;
      clearPress(pressRef);
      // Cancels the synthesized mouse events and click, so the release neither
      // toggles a checkbox nor closes the menu that has just opened.
      if (fired) e.preventDefault();
    },
    onTouchCancel() {
      clearPress(pressRef);
    },
  };
}

function clearPress(pressRef: React.MutableRefObject<Press | null>) {
  if (pressRef.current) clearTimeout(pressRef.current.timer);
  pressRef.current = null;
}
